import { useCallback, useEffect, useEffectEvent, useRef } from 'react';
import { mutate } from 'swr';

import { agentSessionKey, patchAgentSessionLists, promptAgentSession } from '@/api-models/agent-sessions';
import { isPullRequestMutatingTool, revalidateSessionPullRequest } from '@/api-models/sandbox-git';
import { WebSocketConnection, wsUrl } from '@/utils/websocket';

import { type AgentWireEvent, findToolCallName, useAgentSessionStore } from '../stores/agent-session-store';
import type { GoalStartRequest } from '../stores/goal';

/**
 * Keep the sidebar rail's row in step with the turn streaming here.
 *
 * The rail decides "is this agent working" from `last_turn_role` — a trailing user turn means the
 * agent still has the ball. That field only reaches the client on the list poll, so the rail
 * trailed the thread beside it by up to five seconds on every turn boundary: the thread would be
 * streaming tool calls while the row still read "Your turn".
 *
 * These are not guesses. The server persists the user turn before it starts one (`push_prompt`)
 * and flushes the assistant turn on `turn_complete`, so each case below mirrors a write that has
 * already happened — and the poll is still what corrects any drift.
 */
function mirrorTurnToSessionLists(sessionId: string | null, event: AgentWireEvent): void {
  if (!sessionId) return;
  const now = new Date().toISOString();

  switch (event.type) {
    case 'turn_started':
      patchAgentSessionLists(sessionId, { last_turn_role: 'user', last_turn_at: now, last_active_at: now });
      return;
    case 'turn_complete':
      patchAgentSessionLists(sessionId, { last_turn_role: 'assistant', last_turn_at: now, last_active_at: now });
      return;
    case 'agent_exit':
      // The loop is gone, which is what separates "still working" from "stalled". The server
      // clears the column on its way out; this just stops the rail waiting a poll to hear it.
      patchAgentSessionLists(sessionId, { last_active_at: null });
      return;
    default:
      return;
  }
}

/**
 * Opens the coding-agent WebSocket bridge for an agent session and feeds every event into
 * `useAgentSessionStore`. One persistent `coding-agent` process backs the session on the server,
 * so reconnects (page reload, network blip) resume the same live/in-flight state rather than
 * restarting it — the server replays DB-backed history + the in-flight turn buffer on any fresh
 * connection.
 *
 * `enabled` must be false unless the session is `running`. The upgrade handler calls
 * `ensure_session_sandbox_live`, so *connecting this socket resumes a paused sandbox* — and it
 * auto-reconnects forever. Left ungated it would keep waking a sandbox that Daytona keeps
 * auto-stopping, which is the loop this whole feature exists to end.
 */
export function useAgentSessionSocket(sessionId: string | null, enabled: boolean) {
  const connRef = useRef<WebSocketConnection | null>(null);
  const isEnabled = useEffectEvent(() => enabled);

  const reset = useAgentSessionStore((s) => s.reset);
  const handleEvent = useAgentSessionStore((s) => s.handleEvent);
  const addUserTurn = useAgentSessionStore((s) => s.addUserTurn);
  const endTurn = useAgentSessionStore((s) => s.endTurn);
  const failTurn = useAgentSessionStore((s) => s.failTurn);
  const clearApproval = useAgentSessionStore((s) => s.clearApproval);
  const setConnected = useAgentSessionStore((s) => s.setConnected);
  const setAttachState = useAgentSessionStore((s) => s.setAttachState);

  // Reset only when the session actually changes. This is deliberately separate
  // from the connection effect below: the thread stays mounted across
  // pause/resume now, so folding these together would make every `enabled` flip
  // look like a session switch and blank the conversation mid-resume, with
  // nothing left to re-seed it from.
  useEffect(() => {
    reset();
    return reset;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset is a stable zustand action
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId || !enabled) return;

    const conn = new WebSocketConnection({
      url: () => wsUrl(`/api/v1/agent-sessions/${sessionId}/ws`),
      // A socket that dropped because the sandbox stopped must not climb the
      // backoff ladder re-resuming it.
      shouldReconnect: () => isEnabled(),
      onOpen: () => {
        setConnected(true);
        // A fresh socket hasn't attached yet. Without clearing this, a reconnect would inherit
        // the previous socket's `ready` and the composer would enable before the new socket has
        // actually reached the agent.
        setAttachState('connecting');
      },
      onClose: () => {
        setConnected(false);
        // A turn interrupted by a dropped socket never gets its `turn_complete`. The server
        // replays the in-flight turn on reconnect, so settling here is safe and keeps the
        // composer honest in the gap.
        endTurn();
        // The drop is the earliest local hint the sandbox died. This GET runs the
        // server's liveness reconcile, which flips the row to `hibernated` and so
        // clears `enabled` before the first retry would fire.
        void mutate(agentSessionKey(sessionId));
      },
      onMessage: (event) => {
        let parsed: AgentWireEvent | null = null;
        try {
          parsed = JSON.parse(event.data) as AgentWireEvent;
        } catch {
          return;
        }
        if (!parsed) return;

        // Read the tool's name before the event lands: the result carries only the call id, and
        // the lookup wants the `tool_call` that is already in the store.
        const finishedTool =
          parsed.type === 'tool_result' && !parsed.isError
            ? findToolCallName(useAgentSessionStore.getState().turns, parsed.id)
            : null;

        handleEvent(parsed);
        mirrorTurnToSessionLists(sessionId, parsed);

        // The agent's PR tools run in the API process, so their effect is known here the moment
        // they return — no reason to make the header chip and the sessions list wait out a poll.
        if (finishedTool && isPullRequestMutatingTool(finishedTool)) {
          void revalidateSessionPullRequest(sessionId);
        }
      },
    });
    connRef.current = conn;

    return () => {
      conn.dispose();
      connRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleEvent/endTurn/setConnected/setAttachState are stable zustand actions
  }, [sessionId, enabled]);

  const sendPrompt = useCallback(
    (message: string, images: string[] = []): boolean => {
      const trimmed = message.trim();
      if (!trimmed) return false;

      // Only record the optimistic turn if the prompt actually left the browser. Marking a turn
      // in flight for a message the socket silently dropped is what left the composer spinning
      // on a prompt the agent never received.
      if (connRef.current?.send(JSON.stringify({ type: 'prompt', message: trimmed, images }))) {
        addUserTurn(trimmed, images);
        return true;
      }

      if (!sessionId) return false;

      // No live socket, so the sandbox isn't running. POST it instead: that
      // endpoint resumes a paused sandbox transparently, which is what makes
      // "just send a message" the way to wake an idle session back up. It can
      // block for a while behind a cold start — the status line above the
      // composer is what reports progress meanwhile.
      addUserTurn(trimmed, images);
      promptAgentSession(sessionId, trimmed, images)
        .then(() => mutate(agentSessionKey(sessionId)))
        .catch((err: unknown) => {
          failTurn(
            err instanceof Error
              ? `Couldn't reach the sandbox: ${err.message}`
              : "Couldn't reach the sandbox to deliver that message.",
          );
        });
      return true;
    },
    [sessionId, addUserTurn, failTurn],
  );

  const abort = useCallback(() => {
    // Settle locally even if the frame can't go out — otherwise a disconnected socket leaves the
    // user with a Stop button that does nothing.
    if (!connRef.current?.send(JSON.stringify({ type: 'abort' }))) endTurn();
  }, [endTurn]);

  /**
   * Answer a pending approval. The agent blocks the turn until this arrives, so a frame that
   * cannot go out settles the turn rather than leaving the user staring at a live-looking card
   * nothing is listening to.
   */
  const respondToApproval = useCallback(
    (id: string, approved: boolean) => {
      const sent = connRef.current?.send(JSON.stringify({ type: 'approval_response', id, approved }));
      clearApproval();
      if (!sent) failTurn("Couldn't reach the sandbox to answer that request.");
    },
    [clearApproval, failTurn],
  );

  /**
   * Stop one background job, or every running one when `id` is omitted. Returns false when the
   * frame could not go out; nothing is settled locally, because only the job's own
   * `job_finished` proves it stopped.
   */
  const killJobs = useCallback(
    (id?: string): boolean =>
      connRef.current?.send(JSON.stringify({ type: 'kill_jobs', ...(id ? { id } : {}) })) ?? false,
    [],
  );

  /**
   * Start, stop or resume the session's goal. False when the frame could not go out. Nothing is
   * settled locally: the loop answers with `goal` and `goal_note` events — or `goal_rejected` —
   * which every connected tab sees.
   */
  const startGoal = useCallback(
    (goal: GoalStartRequest): boolean =>
      connRef.current?.send(JSON.stringify({ type: 'goal_start', ...goal })) ?? false,
    [],
  );
  const stopGoal = useCallback(
    (): boolean => connRef.current?.send(JSON.stringify({ type: 'goal_stop' })) ?? false,
    [],
  );
  const resumeGoal = useCallback(
    (): boolean => connRef.current?.send(JSON.stringify({ type: 'goal_resume' })) ?? false,
    [],
  );

  return { sendPrompt, abort, respondToApproval, killJobs, startGoal, stopGoal, resumeGoal };
}
